import { FC } from 'react';
import { Eye, CheckCircle, XCircle, Ban, RefreshCw } from 'lucide-react';
import { DataTable, Column } from '../DataTable';
import { TherapistStatusBadge } from './TherapistStatusBadge';
import { getAvailableActions } from '../../../hooks/admin/useAdminTherapists';
import type { AdminTherapistListItem, TherapistStatus } from '../../../api/adminTherapistApi';
import type { Pagination } from '../../../types/admin.types';

interface TherapistTableProps {
  therapists: AdminTherapistListItem[];
  pagination?: Pagination;
  isLoading?: boolean;
  onPageChange?: (page: number) => void;
  onRowClick?: (therapist: AdminTherapistListItem) => void;
  onApprove?: (therapist: AdminTherapistListItem) => void;
  onReject?: (therapist: AdminTherapistListItem) => void;
  onSuspend?: (therapist: AdminTherapistListItem) => void;
  onReactivate?: (therapist: AdminTherapistListItem) => void;
}

export const TherapistTable: FC<TherapistTableProps> = ({
  therapists,
  pagination,
  isLoading = false,
  onPageChange,
  onRowClick,
  onApprove,
  onReject,
  onSuspend,
  onReactivate,
}) => {
  const columns: Column<AdminTherapistListItem>[] = [
    {
      key: 'name',
      header: 'Therapist',
      render: (therapist) => (
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center overflow-hidden">
            {therapist.photoUrl ? (
              <img
                src={therapist.photoUrl}
                alt={therapist.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-sm font-medium text-gray-600 dark:text-slate-400">
                {therapist.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-slate-100">{therapist.name}</p>
            <p className="text-sm text-gray-500 dark:text-slate-400">{therapist.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (therapist) => (
        <TherapistStatusBadge status={therapist.status as TherapistStatus} size="sm" />
      ),
    },
    {
      key: 'specializations',
      header: 'Specializations',
      render: (therapist) => (
        <div className="max-w-[200px]">
          {therapist.specializations.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {therapist.specializations.slice(0, 2).map((spec) => (
                <span
                  key={spec}
                  className="inline-block rounded bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-xs text-blue-800 dark:text-blue-300"
                >
                  {spec}
                </span>
              ))}
              {therapist.specializations.length > 2 && (
                <span className="text-xs text-gray-500 dark:text-slate-400">
                  +{therapist.specializations.length - 2}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400 dark:text-slate-500">None</span>
          )}
        </div>
      ),
    },
    {
      key: 'rating',
      header: 'Rating',
      render: (therapist) =>
        therapist.rating ? (
          <div className="flex items-center gap-1">
            <span className="text-yellow-500">&#9733;</span>
            <span className="text-gray-900 dark:text-slate-100">
              {therapist.rating.toFixed(1)}
            </span>
          </div>
        ) : (
          <span className="text-gray-400 dark:text-slate-500">N/A</span>
        ),
    },
    {
      key: 'createdAt',
      header: 'Joined',
      render: (therapist) => (
        <span className="text-gray-600 dark:text-slate-400">
          {new Date(therapist.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  const renderActions = (therapist: AdminTherapistListItem) => {
    const actions = getAvailableActions(therapist.status as TherapistStatus);

    return (
      <>
        <button
          onClick={() => onRowClick?.(therapist)}
          className="rounded p-1.5 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-slate-200"
          title="View details"
        >
          <Eye className="h-4 w-4" />
        </button>

        {actions.canApprove && (
          <button
            onClick={() => onApprove?.(therapist)}
            className="rounded p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 hover:text-green-700 dark:hover:text-green-300"
            title="Approve"
          >
            <CheckCircle className="h-4 w-4" />
          </button>
        )}

        {actions.canReject && (
          <button
            onClick={() => onReject?.(therapist)}
            className="rounded p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300"
            title="Reject"
          >
            <XCircle className="h-4 w-4" />
          </button>
        )}

        {actions.canSuspend && (
          <button
            onClick={() => onSuspend?.(therapist)}
            className="rounded p-1.5 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:text-orange-700 dark:hover:text-orange-300"
            title="Suspend"
          >
            <Ban className="h-4 w-4" />
          </button>
        )}

        {actions.canReactivate && (
          <button
            onClick={() => onReactivate?.(therapist)}
            className="rounded p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300"
            title="Reactivate"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        )}
      </>
    );
  };

  return (
    <DataTable
      columns={columns}
      data={therapists}
      keyExtractor={(t) => t.id}
      isLoading={isLoading}
      emptyMessage="No therapists found"
      pagination={pagination}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
      actions={renderActions}
    />
  );
};

export default TherapistTable;
